import React from "react";
//#region redux
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
//#endregion
//#region types
import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { Amount } from "models/utilTypes/currencyType";
import { CurrencyEnums } from "models/enums";
import { CashReconciliationListProps } from "models/propTypes/cashReconciliationPropTypes";
import { Customer } from "models/propTypes/userProfilePropTypes";
import { CashReconciliationFormTypes } from "models/formTypes";
import { AppState } from "store";
//#endregion
//#region components
import { TableAsync, Tags, useFilterContext } from "components/common";
import { useCashReconciliationService } from "serviceHooks";
//#endregion
import { UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const CashReconciliationList: React.FC<CashReconciliationListProps> = (props) => {
  const cashReconciliationService = useCashReconciliationService();
  const location = useLocation();
  const navigate = useNavigate();

  const { filter } = useFilterContext<CashReconciliationFormTypes.Filter>();
  const dimensions = useSelector((store: AppState) => store.common.dimensions.cashReconciliationDimensions);

  const getDimensions = (dimensionIds: string[], type: ApiModels.DimensionTypes) => {
    const dimension = dimensions?.find((item) => item.key.compatibilityType === type)?.value;
    return dimension?.filter((c) => c.id !== undefined && dimensionIds.includes(c.id)).map((d) => d.name ?? "") ?? [];
  };

  const filterFunc = React.useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) => cashReconciliationService.reportFilter(baseFilter, filter),
    [filter]
  );

  const onRowClick = (row: ApiModels.CashReconciliationInstanceDescriptor) => {
    navigate(UrlHelper.formatPath(`/CashReconciliation/${row.id}`), {
      state: { background: location, preserveSearch: true },
    });
  };

  return (
    <TableAsync<ApiModels.CashReconciliationInstanceDescriptor>
      properties={{
        date: {
          description: TranslationKeys.Common.Date,
          formatter: (r: ApiModels.CashReconciliationInstanceDescriptor) => new Date(r.date),
          noWrap: true,
          maxWidth: 130,
        },
        ownerName: {
          description: TranslationKeys.CashReconciliation.RegisteredBy,
          formatter: (r: ApiModels.CashReconciliationInstanceDescriptor) => new Customer(r.ownerId, r.ownerName),
          noWrap: true,
          maxWidth: 220,
        },
        costCenter: {
          description: TranslationKeys.Settings.CostCenter,
          formatter: (r: ApiModels.CashReconciliationInstanceDescriptor) => (
            <Tags
              tags={r.dimensions ? getDimensions(r.dimensions, ApiModels.DimensionTypes.CostCenter) : []}
              maxDisplayCount={1}
            />
          ),
          maxWidth: 120,
          noWrap: true,
        },
        project: {
          description: TranslationKeys.Settings.Project,
          formatter: (r: ApiModels.CashReconciliationInstanceDescriptor) => (
            <Tags
              tags={r.dimensions ? getDimensions(r.dimensions, ApiModels.DimensionTypes.Project) : []}
              maxDisplayCount={1}
            />
          ),
          maxWidth: 120,
          noWrap: true,
        },
        difference: {
          description: TranslationKeys.CashReconciliation.Difference,
          formatter: (r: ApiModels.CashReconciliationInstanceDescriptor) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, r.difference),
          width: 200,
          alignment: "right",
          noWrap: true,
          summaryValueFormatter: (r: ApiModels.CashReconciliationInstanceDescriptor) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, r.differenceSummary),
          classification: "amount",
        },
        amount: {
          description: TranslationKeys.Common.Amount,
          formatter: (r: ApiModels.CashReconciliationInstanceDescriptor) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, r.amount),
          width: 200,
          alignment: "right",
          noWrap: true,
          summaryValueFormatter: (r: ApiModels.CashReconciliationInstanceDescriptor) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, r.amountSummary),
          className: cssExports.textBlack,
        },
      }}
      filter={filterFunc}
      pagination="advanced"
      orderByProperty="date"
      orderByDescending
      includeSummary
      rowClickHandler={onRowClick}
      reloadKey={props.reloadKey}
      maxRowCount={10}
    />
  );
};
export default CashReconciliationList;
